import type { FeedbackInfo } from '@compass/client/types/feedback';
import { Edit3, Flag, Loader2, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { useStore } from '@/store';
import { cn } from '@/utils';
import { getRelativeTime } from '@/utils/date';

import { useSubmitEvaluationFeedback, useUpdateEvaluationFeedback } from '../hooks/useFeedback';

interface QcFeedbackPopoverProps {
  evaluationId: string;
  findingIndex?: number;
  feedback?: FeedbackInfo[];
  ariaLabel?: string;
  placeholder?: string;
  triggerClassName?: string;
  userNameById?: Map<string, string>;
}

export const QcFeedbackPopover: React.FC<QcFeedbackPopoverProps> = ({
  evaluationId,
  findingIndex,
  feedback,
  ariaLabel,
  placeholder,
  triggerClassName,
  userNameById,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [editingFeedbackId, setEditingFeedbackId] = useState<string | null>(null);
  const currentUserId = useStore(state => state.user?.id);

  const submitMutation = useSubmitEvaluationFeedback();
  const updateMutation = useUpdateEvaluationFeedback();

  const feedbackItems = useMemo(() => feedback ?? [], [feedback]);
  const editingFeedback = useMemo(
    () => feedbackItems.find(item => item.id === editingFeedbackId),
    [editingFeedbackId, feedbackItems],
  );
  const isSubmitting = submitMutation.isPending || updateMutation.isPending;
  const hasFeedback = feedbackItems.length > 0;
  const isEditing = !!editingFeedback;
  const canEditFeedback = useCallback(
    (item: FeedbackInfo) => !item.created_by || item.created_by === currentUserId,
    [currentUserId],
  );
  const initialText = editingFeedback?.text ?? '';
  const trimmedText = text.trim();
  const isDirty = isEditing ? text !== initialText : trimmedText.length > 0;
  const hasValidText = trimmedText.length > 0;
  const title = 'Feedback';
  const resolvedPlaceholder =
    placeholder ||
    "Describe the issue — do you agree or disagree with the AI's answer? What's wrong and why?";
  const resolvedAriaLabel =
    ariaLabel ||
    (findingIndex === undefined
      ? 'Give feedback on this evaluation'
      : 'Give feedback on this question');
  const submitButtonLabel = isEditing ? 'Update' : 'Add feedback';
  const isSubmitDisabled = !hasValidText || !isDirty || isSubmitting;

  const resetState = useCallback(() => {
    setText('');
    setEditingFeedbackId(null);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isSubmitting) return;
      setOpen(nextOpen);
    },
    [isSubmitting],
  );

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    setOpen(false);
  }, [isSubmitting]);

  const handleEditStart = useCallback(
    (item: FeedbackInfo) => {
      if (!canEditFeedback(item) || isSubmitting || !item.id) return;
      setText(item.text ?? '');
      setEditingFeedbackId(item.id);
    },
    [canEditFeedback, isSubmitting],
  );

  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    resetState();
    if (!isEditing) {
      setOpen(false);
    }
  }, [isEditing, isSubmitting, resetState]);

  const handleSubmit = useCallback(() => {
    if (trimmedText.length === 0 || isSubmitting) return;

    if (editingFeedback?.id) {
      updateMutation.mutate(
        {
          evaluationId,
          feedbackId: editingFeedback.id,
          text: trimmedText,
        },
        { onSuccess: () => resetState() },
      );
    } else {
      submitMutation.mutate(
        {
          evaluationId,
          findingIndex,
          text: trimmedText,
        },
        { onSuccess: () => resetState() },
      );
    }
  }, [
    editingFeedback?.id,
    findingIndex,
    isSubmitting,
    evaluationId,
    resetState,
    submitMutation,
    trimmedText,
    updateMutation,
  ]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 w-8 shrink-0 px-0 inline-flex items-center justify-center rounded-md transition-colors',
            hasFeedback
              ? 'text-notice-foreground hover:bg-notice hover:text-notice-foreground'
              : 'text-muted-foreground/50 hover:bg-muted hover:text-muted-foreground',
            triggerClassName,
          )}
          aria-label={resolvedAriaLabel}
        >
          <Flag className="h-3.5 w-3.5" fill={hasFeedback ? 'currentColor' : 'none'} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
        sticky="partial"
        className="flex w-96 max-w-[calc(100vw-2rem)] overflow-hidden p-0"
        style={{
          maxHeight:
            'min(32rem, var(--radix-popover-content-available-height), calc(100vh - 2rem))',
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col p-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold">
              {title}
              {hasFeedback ? ` (${feedbackItems.length})` : ''}
            </h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
              className="h-8 w-8 shrink-0 p-0"
              aria-label="Close feedback"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden">
            {hasFeedback ? (
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="space-y-2 pb-3">
                  {feedbackItems.map(item => {
                    const feedbackAuthorName =
                      (item.created_by && userNameById?.get(item.created_by)) || 'QC Reviewer';
                    const metaParts = [feedbackAuthorName];

                    if (item.created_at) {
                      metaParts.push(getRelativeTime(item.created_at));
                    }

                    if (item.updated_at && item.created_at && item.updated_at !== item.created_at) {
                      metaParts.push('edited');
                    }

                    const isEditingThisItem = item.id === editingFeedbackId;

                    return (
                      <div
                        key={item.id || `${item.created_at}-${item.created_by}-${item.text}`}
                        className={cn(
                          'rounded-xl border border-border/80 bg-background px-3 py-2.5 shadow-xs',
                          isEditingThisItem && 'border-primary/40 ring-1 ring-primary/20',
                        )}
                      >
                        <div className="relative min-w-0">
                          <div className="min-w-0 pr-6">
                            <p className="text-xs text-muted-foreground">{metaParts.join(' · ')}</p>
                          </div>
                          {canEditFeedback(item) && item.id ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditStart(item)}
                              className="absolute right-0 top-0 h-5 w-5 p-0"
                              aria-label="Edit feedback"
                              disabled={isSubmitting}
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </div>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                          {item.text || '--'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className={cn('shrink-0 space-y-2', hasFeedback && 'border-t pt-3')}>
              {isEditing && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="h-7 px-2 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              )}

              <Textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={resolvedPlaceholder}
                className="min-h-20 max-h-28 resize-none overflow-y-auto rounded-xl border-border/80 text-sm leading-relaxed text-muted-foreground placeholder:text-muted-foreground/90"
                disabled={isSubmitting}
              />

              {isEditing && !isDirty && (
                <p className="text-xs text-muted-foreground">Edit the feedback to enable update.</p>
              )}

              <div className="flex gap-2">
                {!isEditing && isDirty ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    disabled={isSubmitting}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="default"
                  className="flex-1"
                  disabled={isSubmitDisabled}
                  onClick={handleSubmit}
                >
                  {submitMutation.isPending || updateMutation.isPending ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  {submitMutation.isPending
                    ? 'Submitting...'
                    : updateMutation.isPending
                      ? 'Updating...'
                      : submitButtonLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
